import type { NotesTable } from "@msuite/utils/types";
import type { AppSubjectName } from "./strategies/resource-map";

/**
 * The subject whose `read` rule decides who sees a parent's notes, per join table.
 *
 * There is deliberately no `read Note` rule: a `notes` row carries no reference to what it is
 * about, so reading follows the parent instead. That makes this map part of the permission model
 * rather than a routing detail — it is the only place that says which subject a note answers to,
 * and both sides of the wire need it. The server checks it before it serves a note; a client that
 * renders a note surface asks the same question first, because a form whose submit is a 403 is
 * worse than no form.
 *
 * `null` is a parent whose visibility is deliberately every member's — the server still proves
 * the parent belongs to the caller's organisation (a missing gate is not a missing tenancy
 * check) and asks nothing further. Ticket assignments are that case: Ticket is not a subject,
 * because every member reads every ticket.
 *
 * The server's registry (`apps/server/src/handlers/notes/registry.ts`) is where the choice is
 * argued entry by entry, next to the statement that loads the parent, and
 * `handlers/notes/notes.test.ts` compares the two so they cannot drift.
 */
export const NOTE_PARENT_SUBJECTS = {
	contactNotes: "Contact",
	incomingInvoiceNotes: "IncomingInvoice",
	orderNotes: "Stock",
	organizationPartnershipNotes: "Partner",
	organizationServiceNotes: "Service",
	projectNotes: "Project",
	projectTimelineNoteNotes: "Project",
	protocolNotes: "Protocol",
	returnOrderNotes: "Stock",
	serviceCalculationItemNotes: "Calculation",
	serviceCalculationNotes: "Calculation",
	suborderNotes: "Stock",
	substitutionPlanNotes: "Absence",
	// Ticket is not a subject: every member of the organisation reads every ticket, so a note on
	// a ticket assignment asks nothing beyond the tenancy check every parent gets.
	ticketAssignmentNotes: null,
	transferCarrierNotes: "Stock",
	userNotes: "User",
	vehicleNotes: "Vehicle",
	warehouseZoneNotes: "Stock",
	wikiPageNotes: "Wiki",
} as const satisfies Record<NotesTable, AppSubjectName | null>;

/** The subjects a note can answer to, without the `null` of a parent that has none yet. */
export type NoteParentSubject = Exclude<(typeof NOTE_PARENT_SUBJECTS)[NotesTable], null>;
